/**
 * Declarative map: WS event type → queryKey prefixes to invalidate.
 *
 * Uses React Query's partial-key prefix matching — e.g. ['wallet'] invalidates
 * every query whose key starts with 'wallet'. This is intentionally broad per
 * key: React Query will only network-fetch queries that have active observers
 * (i.e. components currently mounted on screen). Pages the user isn't on get
 * marked stale and refetch lazily when they mount — no wasted requests.
 *
 * Adding a new WS event: add an entry here and the invalidation is automatic.
 * No need to touch RealtimeGateProvider unless a toast is also required.
 */

import type { QueryKey } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

const INVALIDATION_MAP: Record<string, QueryKey[]> = {
  // ── Balance channel ────────────────────────────────────────────────────────
  deposit_received: [
    ['accounts', 'primary'],
    ['wallet', 'balance'],
    ['wallet', 'movements'],
    ['wallet', 'summary'],
  ],
  balance_updated: [
    ['accounts', 'primary'],
    ['wallet', 'balance'],
    // BalanceChannel ahora incluye payload.limits_usage en cada balance_updated
    // (tankef-pay-service PR #360). Invalida el query para que el snapshot
    // del cache se refresque y los componentes que dependen de él (badges
    // de límite, banners) reaccionen. El polling de useTransactionLimitsUsage
    // se eliminó en este mismo PR.
    ['wallet', 'transaction-limits', 'usage'],
    // Los holds viven en 'account.<id>.holds'. Cuando el balance cambia
    // suele venir de hold_* pero también de payment_completed/withdrawal:
    // invalidar para que pantallas con holds visibles refetcheen.
    ['account'],
  ],
  transfer_debited: [
    ['accounts', 'primary'],
    ['wallet', 'balance'],
    ['wallet', 'movements'],
    ['wallet', 'summary'],
    ['wallet', 'transaction-limits', 'usage'],
    ['transfers'],
  ],
  withdrawal_completed: [
    ['accounts', 'primary'],
    ['wallet', 'balance'],
    ['wallet', 'movements'],
    ['wallet', 'summary'],
  ],
  hold_created: [
    ['accounts', 'primary'],
    ['wallet', 'balance'],
    ['account'], // useAccountHolds(accountId, status) — prefijo cubre ambos
    ['wallet', 'transaction-limits', 'usage'],
  ],
  hold_released: [
    ['accounts', 'primary'],
    ['wallet', 'balance'],
    ['wallet', 'movements'],
    ['account'],
    ['wallet', 'transaction-limits', 'usage'],
  ],
  hold_settled: [
    ['accounts', 'primary'],
    ['wallet', 'balance'],
    ['wallet', 'movements'],
    ['wallet', 'summary'],
    ['account'],
    ['wallet', 'transaction-limits', 'usage'],
  ],
  account_updated: [
    ['accounts'], // covers ['accounts', 'primary'] + ['accounts'] list
    ['account'], // prefix → all ['account', id, 'clabe'], ['account', id, 'holds'], …
    ['wallet', 'balance'],
  ],

  // ── KYC channel ────────────────────────────────────────────────────────────
  // BE emite: kyc_started, kyc_document_uploaded, kyc_verification_progress,
  // kyc_approved, kyc_level_upgraded, kyc_pending_review, kyc_rejected.
  // Los eventos kyc_document_rejected/verified/required del legacy FE map NO
  // existen en BE — el BE actual usa kyc_rejected genérico y kyc_update
  // (que mapeamos al genérico kyc_status invalidation).
  kyc_started: [['party']],
  kyc_document_uploaded: [['party']],
  kyc_verification_progress: [['party']],
  kyc_approved: [['party'], ['wallet', 'transaction-limits']],
  kyc_level_upgraded: [['party'], ['wallet', 'transaction-limits']],
  kyc_pending_review: [['party']],
  kyc_rejected: [['party']],
  kyc_update: [['party']],

  // ── Onboarding channel — esign_update (tankef-pay-service PR #360) ────────
  // Disparado por JAAK webhook signature.completed/failed/expired. Reemplaza
  // el polling de useOnboardingContractsEsignStatus (5s) eliminado en este PR.
  esign_update: [['onboarding', 'contracts', 'esign-status'], ['onboarding']],

  // ── Payment channel ────────────────────────────────────────────────────────
  // BE emite: payment_completed, payment_progress, payment_error, current_status.
  // Los eventos payment_created/failed/processing del legacy map NO se emiten
  // por el BE — eran nombres pre-175e0b61. Mantenemos solo los reales.
  payment_completed: [
    ['accounts', 'primary'],
    ['wallet', 'balance'],
    ['wallet', 'movements'],
    ['wallet', 'summary'],
    ['payment-orders'],
    ['payment-order'], // usePaymentOrderStatus core-react: ['payment-order', id, 'status']
    ['payment-order-status'], // duplicado apps/web: ['payment-order-status', id]
  ],
  payment_progress: [['payment-orders'], ['payment-order'], ['payment-order-status']],
  payment_error: [['payment-orders'], ['payment-order'], ['payment-order-status']],
  current_status: [['payment-orders'], ['payment-order'], ['payment-order-status']],

  // ── Notifications channel ──────────────────────────────────────────────────
  // BE emite: new_notification, notification_read, unread_count_updated,
  // batch_notifications. NotificationsChannel.broadcast_system_alert también
  // emite system_alert (canal técnico `system_alerts:tenant_id`).
  new_notification: [['notifications']],
  notification_read: [['notifications']],
  unread_count_updated: [['notifications', 'unread-count']],
  batch_notifications: [['notifications']],

  // ── System alerts channel (tenant-wide) ────────────────────────────────────
  // BE emite: system_alert (con payload.alert_type — e.g. spei_outage).
  // No invalida queries; el handler en RealtimeGateProvider muestra el toast/
  // banner. Entry presente para coherencia del INVALIDATION_MAP.
  system_alert: [['system-status']],

  // ── Relay channel (tandas — 18 eventos del BE) ─────────────────────────────
  // Suscripción scoped por relay_id (ver useRelayUpdates). Invalida tanto
  // ['relays'] (listas de tablas) como ['relays', relayId] (detail/payments/
  // participants/invitations/turn-requests) para que cualquier vista mounted
  // refetchee. React Query no dispara network si el query no tiene observers.
  participant_joined: [['relays'], ['tanda', 'my-status']],
  participant_removed: [['relays'], ['tanda', 'my-status']],
  participants_reordered: [['relays']],
  relay_activated: [['relays'], ['tanda', 'my-status']],
  relay_completed: [['relays'], ['tanda', 'my-status']],
  relay_cancelled: [['relays'], ['tanda', 'my-status']],
  relay_updated: [['relays']],
  payment_updated: [['relays']],
  payment_received: [['relays']],
  payment_requested: [['relays']],
  distribution_requested: [['relays']],
  invitation_sent: [['relays'], ['tanda', 'my-status']],
  invitation_accepted: [['relays'], ['tanda', 'my-status']],
  invitation_revoked: [['relays'], ['tanda', 'my-status']],
  turn_requested: [['relays']],
  turn_request_approved: [['relays']],
  turn_request_rejected: [['relays']],
  tanda_recalculated: [['relays']],

  // ── Savings & Investment updates ────────────────────────────────────────────
  // BE emite: savings_updated / investment_updated tras aportación/capitalización.
  // Invalida portfolio y detail queries para refrescar balances/rendimientos.
  savings_updated: [['savings'], ['savings', 'portfolio']],
  investment_updated: [['investments'], ['investments', 'portfolio']],
};

/**
 * Invalidates all queryKey prefixes registered for the given WS event type.
 * Safe to call with unknown event types — unknown types are silently ignored.
 */
export function invalidateByEvent(queryClient: QueryClient, eventType: string): void {
  const keys = INVALIDATION_MAP[eventType];
  if (!keys?.length) return;
  for (const queryKey of keys) {
    void queryClient.invalidateQueries({ queryKey });
  }
}
